
import { useEffect, useRef, useState } from "react";
import { CharDisplay, CharStream } from "./charStream";
import { TranscriptStore } from "./store";
import { CardView, toCards } from "./view";

const REVEAL_INTERVAL_MS = 16;
const REVEAL_STEP = 2;

export function useTranscriptStream(store: TranscriptStore): CardView[] {
  const charStreamRef = useRef<CharStream | null>(null);
  if (charStreamRef.current === null) {
    charStreamRef.current = new CharStream({ step: REVEAL_STEP });
  }
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const animatingIdRef = useRef<string | null>(null);
  const [display, setDisplay] = useState<Record<string, CharDisplay>>(() => {
    const res = charStreamRef.current!.advance(store.getState());
    animatingIdRef.current = res.animatingId;
    return res.display;
  });

  useEffect(() => {
    const advanceOnce = (): boolean => {
      const { display: next, animating, animatingId } = charStreamRef.current!.advance(store.getState());
      animatingIdRef.current = animatingId;
      setDisplay(next);
      return animating;
    };
    const ensureTimer = (): void => {
      if (timerRef.current) return;
      timerRef.current = setInterval(() => {
        const animating = advanceOnce();
        if (!animating && timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      }, REVEAL_INTERVAL_MS);
    };

    const unsubscribe = store.subscribe(() => {
      advanceOnce();
      ensureTimer();
    });
    advanceOnce();
    ensureTimer();

    return () => {
      unsubscribe();
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [store]);

  return toCards(store.getState(), display, animatingIdRef.current);
}
